import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Send, Sparkles, Wine, ArrowRight } from "lucide-react";

const STARTERS = [
  "Cerco un Primitivo sotto 4€, adatto alla GDO tedesca, con etichetta moderna.",
  "Suggerisci 3 vini premium per ristorazione HoReCa export in Svizzera",
  "Quale bianco siciliano consigli per volume alto?",
];

const eur = (n) => `€${Number(n || 0).toFixed(2).replace(".", ",")}`;

function WineCard({ wine }) {
  return (
    <Link
      to={`/b2b/wines/${wine.id}`}
      data-testid={`somm-wine-${wine.id}`}
      className="group flex gap-3 bg-[hsl(var(--background))] border border-[hsl(var(--accent))]/30 hover:border-[hsl(var(--accent))] p-3 rounded-sm transition-colors"
    >
      <div className="w-20 h-20 shrink-0 bg-white rounded-sm overflow-hidden border border-[hsl(var(--border))]">
        {wine.image ? (
          <img src={wine.image} alt={wine.name} className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[hsl(var(--muted-foreground))]"><Wine className="h-6 w-6" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--accent))]">{wine.denomination} · {wine.region}</div>
        <div className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{wine.name} <span className="text-[hsl(var(--muted-foreground))] font-normal">{wine.vintage}</span></div>
        <div className="mt-1 flex items-center justify-between gap-2 text-xs">
          <div className="text-[hsl(var(--foreground))]">
            <span className="text-[hsl(var(--accent))] font-bold">{eur(wine.price_per_bottle)}</span>
            <span className="text-[hsl(var(--muted-foreground))]">/btl · {eur(wine.price_per_case)}/case</span>
          </div>
          <span className="text-[hsl(var(--accent))] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
            Vedi <ArrowRight className="h-3 w-3" />
          </span>
        </div>
        <div className="mt-0.5 text-[10px] text-[hsl(var(--muted-foreground))]">
          Stock: {Number(wine.stock_available || 0).toLocaleString("it-IT")} btl · margine {wine.suggested_margin}%
        </div>
      </div>
    </Link>
  );
}

export default function B2BSommelier() {
  const { lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/wines/sommelier", { message: msg, session_id: sessionId, lang });
      setSessionId(r.data.session_id);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: r.data.answer,
        wines: r.data.recommended_wines || [],
      }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Errore AI, riprova.", wines: [] }]);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="b2b-sommelier-page" className="container-xl py-10 flex flex-col h-[calc(100vh-5rem)]">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2 flex items-center gap-2"><Sparkles className="h-3 w-3"/>AI Powered · GPT-5.2</div>
        <h1 className="editorial-heading text-4xl md:text-5xl text-[hsl(var(--foreground))]">AI Sommelier B2B</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">Chiedi come a un sommelier esperto: trova il vino giusto per il tuo mercato.</p>
      </div>

      <div className="mt-6 flex-1 overflow-y-auto bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-5 space-y-5">
        {messages.length === 0 && (
          <div className="text-[hsl(var(--muted-foreground))]">
            <div className="mb-3 text-sm">Prova con uno di questi:</div>
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s, i) => (
                <button key={i} data-testid={`sommelier-starter-${i}`} onClick={() => send(s)} className="text-left text-sm border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] px-3 py-2 rounded-sm hover:bg-[hsl(var(--accent))]/10">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`somm-msg-${i}`} className={`${m.role === "user" ? "ml-auto max-w-[85%] bg-[hsl(var(--accent))] text-black p-4" : "max-w-full bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] p-4"} rounded-sm`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70 mb-2">{m.role === "user" ? "Tu" : "Sommelier AI"}</div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</div>

            {m.role === "assistant" && m.wines && m.wines.length > 0 && (
              <div className="mt-5 pt-5 border-t border-[hsl(var(--accent))]/20">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold mb-3 flex items-center gap-2">
                  <Wine className="h-3 w-3" /> Vini consigliati ({m.wines.length})
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {m.wines.map((w) => <WineCard key={w.id} wine={w} />)}
                </div>
              </div>
            )}
          </div>
        ))}
        {loading && <div className="text-sm text-[hsl(var(--muted-foreground))] italic">Analisi in corso…</div>}
      </div>

      <div className="mt-4 flex gap-2">
        <Textarea
          data-testid="sommelier-input"
          rows={2}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Es: Cerco un Barolo per ristorazione HoReCa francese, sotto 50€/btl..."
          className="flex-1 bg-[hsl(var(--card))] border-[hsl(var(--border))] rounded-sm"
        />
        <Button data-testid="sommelier-send" onClick={() => send()} disabled={loading} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 rounded-sm gap-2">
          <Send className="h-4 w-4"/>Invia
        </Button>
      </div>
    </div>
  );
}
